/**
 * The permanent audit trail as the History tab receives it.
 *
 * Every name on an entry is a snapshot taken when the change was recorded, not a live lookup: the
 * user, the item, the blueprint field and the attachment can all be renamed or deleted afterwards,
 * and history that rewrites itself when they are is not history. Nothing here needs resolving
 * against another store before it can be rendered.
 */

/**
 * Must stay in sync with AmoebaConstants.AuditHistoryConstants.Scopes on the backend.
 *
 * Declared as a constant with the type derived from it, rather than as a bare union, so that nothing
 * has to repeat the literal. `as const` keeps each value's literal type, so the derived union is
 * exactly what it was and a `switch` over it still narrows.
 */
export const AuditHistoryScopes = {
  Node: 'Node',
  Steps: 'Steps',
  Attachments: 'Attachments',
  Run: 'Run',
} as const;

export type AuditHistoryScope = (typeof AuditHistoryScopes)[keyof typeof AuditHistoryScopes];

/** Must stay in sync with AmoebaConstants.AuditHistoryConstants.Actions on the backend. */
export const AuditHistoryActions = {
  Created: 'Created',
  Updated: 'Updated',
  Renamed: 'Renamed',
  Archived: 'Archived',
  Unarchived: 'Unarchived',
  Deleted: 'Deleted',
  AddedToLabel: 'AddedToLabel',
  RemovedFromLabel: 'RemovedFromLabel',
  Moved: 'Moved',
  /** Recorded against the *parent* when it gains or loses a child - a sub-task on a story. */
  ChildAdded: 'ChildAdded',
  ChildRemoved: 'ChildRemoved',
  AttachmentAdded: 'AttachmentAdded',
  AttachmentRemoved: 'AttachmentRemoved',
  StepAdded: 'StepAdded',
  StepUpdated: 'StepUpdated',
  StepDeleted: 'StepDeleted',
  StepReordered: 'StepReordered',
  StepAttachmentAdded: 'StepAttachmentAdded',
  StepAttachmentRemoved: 'StepAttachmentRemoved',
  RunUpdated: 'RunUpdated',
} as const;

export type AuditHistoryAction = (typeof AuditHistoryActions)[keyof typeof AuditHistoryActions];

/**
 * Friendly labels for the event-type filter, keyed by the raw action the backend stores.
 *
 * Independent of `transaction()`'s compact verb tag (EditPanelHistoryPaneComponent) - the badge
 * stays short so a long trail's verbs line up ("Added", "Removed"), while a filter dropdown has
 * room to be specific about what an option actually selects ("Added Label" is a filter someone can
 * pick; a badge reading "Added Label" on every row next to an attachment row also reading "Added"
 * would be the inconsistency, not this one). ChildAdded/ChildRemoved are the exception: those two
 * *do* match their badge text one-for-one, because the whole point of naming them was to make the
 * badge itself say "child" - see the History tab notes in this repo's CLAUDE.md.
 */
export const AUDIT_HISTORY_ACTION_LABELS: Readonly<Record<AuditHistoryAction, string>> = {
  Created: 'Created',
  Updated: 'Updated',
  Renamed: 'Renamed',
  Archived: 'Archived',
  Unarchived: 'Restored',
  Deleted: 'Deleted',
  AddedToLabel: 'Added Label',
  RemovedFromLabel: 'Removed Label',
  Moved: 'Moved',
  ChildAdded: 'Added Child',
  ChildRemoved: 'Removed Child',
  AttachmentAdded: 'Added Attachment',
  AttachmentRemoved: 'Removed Attachment',
  StepAdded: 'Added Step',
  StepUpdated: 'Updated Step',
  StepDeleted: 'Deleted Step',
  StepReordered: 'Reordered Steps',
  StepAttachmentAdded: 'Added Step Attachment',
  StepAttachmentRemoved: 'Removed Step Attachment',
  RunUpdated: 'Updated Run',
};

export interface IAuditHistoryChange {
  /** Blueprint field id, a step column name, or a reserved key such as `__name`. */
  fieldId: string | null;
  /** Display label snapshotted at the time of the change. */
  fieldName: string | null;
  /** Display text, resolved when the change was recorded — "High", not the option's id. */
  oldValue: string | null;
  newValue: string | null;
  /**
   * The raw stored value behind the display text where the two differ — an option id, a user id, an
   * attachment id. Null when the value needed no resolution. Render `oldValue`/`newValue`; these are
   * here so a row can still be matched back to the blueprint or the user.
   */
  oldValueId: string | null;
  newValueId: string | null;
  /** Values are capped at 4096 characters on write; this says the stored text was cut. */
  oldValueTruncated: boolean;
  newValueTruncated: boolean;
}

export interface IAuditHistoryEntry {
  id: string;

  entityType: string;
  entityId: string;
  /** e.g. "BOBS-12" — snapshotted, so it survives the item being deleted. */
  entityKey: string | null;
  entityName: string | null;

  action: AuditHistoryAction;
  scope: AuditHistoryScope;
  isLifecycle: boolean;

  nodeId: string | null;
  labelId: string | null;
  /**
   * Which label the change was made under — the same node is often shared across several labels
   * (e.g. shown on more than one board), and this is what tells them apart in the trail. Snapshotted
   * at write time where the writer had the label in hand; resolved by id as a server-side fallback
   * otherwise, falling back further to the raw label id if even that cannot resolve (a hard-deleted
   * label). Null only when the row carries no label at all.
   */
  labelName: string | null;
  /**
   * Whether the label named above is archived right now. Not a snapshot like every other field on
   * this entry — archiving is reversible and normally happens long after the change was recorded, so
   * the backend resolves this fresh on every read rather than storing it. Drives the struck-through
   * rendering of `labelName` in the History tab.
   */
  isLabelArchived: boolean;
  projectId: string | null;

  /** Run-scoped entries only: which label's run row changed. */
  runLabelId: string | null;
  runLabelName: string | null;

  /** Step-scoped entries only. */
  stepNumber: number | null;

  actorUserId: string | null;
  actorDisplayName: string | null;
  actorUserName: string | null;

  /**
   * Which application recorded the change - see AUDIT_HISTORY_SOURCE_APP_LABELS for the ids that
   * carry a friendly name. Deliberately a loose string rather than a union: it is whatever the
   * backend stored, and a value this build has never heard of must still render rather than break
   * the type.
   */
  sourceApp: string | null;

  /** Shared by every entry one bulk operation produced, so the UI can group them. */
  correlationId: string | null;

  /** ISO-8601 UTC. */
  occurredAt: string;

  changes: IAuditHistoryChange[];
}

/**
 * Friendly names for the applications that can record history, keyed by the lower-cased id the
 * backend stores.
 *
 * `amoeba` and `catchhub` are the product itself, so they are deliberately absent: a change made
 * here is not "via" anywhere and naming a source would be noise on every ordinary row. An id that
 * is not listed renders as-is - better a raw token than a silently dropped provenance.
 */
export const AUDIT_HISTORY_SOURCE_APP_LABELS: Readonly<Record<string, string>> = {
  chimera: 'Jira',
  chrysalis: 'Enterprise Tester',
  smartplanner: 'SmartPlanner',
  apikey: 'API',
};

/** Source apps that are the product itself, so a change from one shows no "via" attribution. */
export const AUDIT_HISTORY_INTERNAL_SOURCE_APPS: readonly string[] = ['amoeba', 'catchhub'];

/** Optional narrowing sent with a history request. */
export interface IAuditHistoryQuery {
  scope?: AuditHistoryScope | null;
  /** Users whose changes to include. Empty/absent means everyone - OR'd together, not intersected. */
  actorUserIds?: string[] | null;
  /** Labels a change was recorded in - OR'd together. An entry with no label is excluded, not matched. */
  labelIds?: string[] | null;
  /**
   * Free text matched against the parts of a row a reader can see - the actor's name, the item's key
   * and name, and each change's field name and before/after values. Not the action: the scope chips
   * already answer that.
   */
  searchText?: string | null;
  /** ISO-8601 UTC. */
  fromDate?: string | null;
  toDate?: string | null;
  /** Event types to include. Empty/absent means every action - OR'd together, not intersected. */
  actions?: AuditHistoryAction[] | null;
}

/** One entry in a filter dropdown. */
export interface IAuditHistoryFilterOption {
  id: string;
  /**
   * Can arrive null on the wire - the label endpoint falls back to the id when a name cannot be
   * resolved, but an actor whose account was deleted has no such fallback server-side. The component
   * normalizes this to the id before rendering, so the option is always selectable on screen even
   * though this type does not guarantee it.
   */
  label: string | null;
}

/**
 * What this item's filter dropdowns offer: who has actually changed it, and which labels those
 * changes were recorded in.
 *
 * Fetched from the API rather than derived from the entries on screen. The tab holds one page, so a
 * list built from it would omit everyone further back than the reader has scrolled - and a filter
 * whose options are incomplete is worse than none, because nothing says it is short.
 */
export interface IAuditHistoryFilterOptions {
  actors: IAuditHistoryFilterOption[];
  labels: IAuditHistoryFilterOption[];
  /**
   * The distinct event types this item's history actually contains - bare action strings, not
   * id/label pairs: unlike actors and labels there is nothing to resolve, since every action already
   * has a fixed display label (AUDIT_HISTORY_ACTION_LABELS). A step/run action can only appear here
   * for a Test item, because only a Test's history can ever record one.
   */
  actions: AuditHistoryAction[];
}
